import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, AlertCircle, CheckCircle, Settings, Users, MapPin } from 'lucide-react';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Team } from '../../types';

export const TeamSettings: React.FC = () => {
  const navigate = useNavigate();
  const { team: authTeam, refreshUser, isSuperAdmin } = useAuth();
  const [team, setTeam] = useState<Team | null>(null);
  const [stats, setStats] = useState<{ userCount: number; posCount: number; visitCount: number } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    billing_email: '',
  });

  useEffect(() => {
    const fetchTeamInfo = async () => {
      try {
        setIsLoading(true);
        const response = await api.get('/team');
        setTeam(response.data.team);
        setStats(response.data.stats);
        setFormData({
          name: response.data.team.name,
          billing_email: response.data.team.billing_email || '',
        });
      } catch (err) {
        console.error('Failed to fetch team info:', err);
        setError('Failed to load team information');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTeamInfo();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsSubmitting(true);

    try {
      await api.patch('/team/settings', formData);
      setSuccessMessage('Team settings updated successfully');
      // Refresh user data to update team name in context
      await refreshUser();
    } catch (err: any) {
      console.error('Failed to update team:', err);
      setError(err.response?.data?.error || 'Failed to update team settings');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="px-4 py-6 max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">Team information not available</p>
        </div>
      </div>
    );
  }

  const getPlanBadgeColor = (plan: string) => {
    switch (plan) {
      case 'enterprise': return 'bg-purple-100 text-purple-800';
      case 'growth': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="px-4 py-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Settings size={28} />
          Team Settings
        </h1>
        <p className="text-sm text-gray-500 mt-1">Manage your team's configuration</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3 text-green-700">
          <CheckCircle size={20} />
          {successMessage}
        </div>
      )}

      {/* Team Overview */}
      <section className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Team Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Users size={24} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Users</p>
              <p className="text-xl font-semibold">
                {stats?.userCount || 0} / {team.max_users}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <MapPin size={24} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Points of Sale</p>
              <p className="text-xl font-semibold">
                {stats?.posCount || 0} / {team.max_pos}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Settings size={24} className="text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Plan</p>
              <p className="text-xl font-semibold capitalize">{team.plan}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Editable Settings */}
      <form onSubmit={handleSubmit}>
        <section className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Team Information</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Team Name</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Your Team Name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Billing Email</label>
              <input
                type="email"
                value={formData.billing_email}
                onChange={(e) => setFormData(prev => ({ ...prev, billing_email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="billing@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Team Slug</label>
              <input
                type="text"
                value={team.slug}
                disabled
                className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed font-mono"
              />
              <p className="mt-1 text-xs text-gray-500">Slug cannot be changed after creation</p>
            </div>
          </div>
        </section>

        {/* Subscription Info (Read-only) */}
        <section className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Subscription</h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Plan</span>
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${getPlanBadgeColor(team.plan)}`}>
                {team.plan}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Status</span>
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                team.subscription_status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
              }`}>
                {team.subscription_status}
              </span>
            </div>
            {team.trial_ends_at && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Trial Ends</span>
                <span className="text-sm font-medium text-gray-900">
                  {new Date(team.trial_ends_at).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
          {!isSuperAdmin && (
            <p className="mt-4 text-sm text-gray-500">
              To upgrade your plan or update billing details, please contact support.
            </p>
          )}
        </section>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <Save size={20} />
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TeamSettings;
